import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import LoadingState from '../components/LoadingState';
import ErrorState from '../components/ErrorState';
import { settings, stores } from '../../services/apiClient';

function digitsOnly(value) {
  return String(value || '').replace(/\D/g, '');
}

function formatPhone(value) {
  const digits = digitsOnly(value);
  if (!digits) return '';
  if (digits.length === 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return digits;
}

function normalizeKey(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

function parseCity(address) {
  const raw = String(address || '').trim();
  if (!raw) return '';
  const parts = raw.split(/[,\|]/).map((part) => part.trim()).filter(Boolean);
  if (!parts.length) return '';

  const ufCodes = new Set([
    'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
    'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
    'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
  ]);

  let regionIndex = -1;

  for (let i = parts.length - 1; i >= 0; i -= 1) {
    const match = parts[i].match(/(.+?)\s*[-/]\s*([A-Za-z]{2})(?:\b|[^A-Za-z].*)$/);
    if (match) {
      const uf = String(match[2] || '').toUpperCase();
      if (ufCodes.has(uf)) return match[1].trim();
    }
  }

  for (let i = parts.length - 1; i >= 0; i -= 1) {
    const match = parts[i].match(/\b([A-Za-z]{2})\b/);
    if (!match) continue;
    const uf = String(match[1] || '').toUpperCase();
    if (ufCodes.has(uf)) {
      regionIndex = i;
      break;
    }
  }

  const cepIndex = parts.findIndex((part) => /\b\d{5}-?\d{3}\b/.test(part));
  if (cepIndex >= 0 && cepIndex + 1 < parts.length) {
    return parts[cepIndex + 1];
  }
  if (regionIndex > 0) {
    return parts[regionIndex - 1];
  }
  if (parts.length >= 2) return parts[parts.length - 2];
  return parts[parts.length - 1];
}

export default function AdminSettings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const [inputNumber, setInputNumber] = useState('');
  const [numbers, setNumbers] = useState([]);
  const [defaultNumber, setDefaultNumber] = useState('');
  const [promoEnabled, setPromoEnabled] = useState(false);
  const [promoCode, setPromoCode] = useState('CORREFOOD');
  const [promoPercent, setPromoPercent] = useState(0);
  const [promoApplyOnPromotionalProducts, setPromoApplyOnPromotionalProducts] = useState(true);
  const [storesData, setStoresData] = useState([]);
  const [promoRules, setPromoRules] = useState([]);
  const [ruleMode, setRuleMode] = useState('store');
  const [ruleStoreId, setRuleStoreId] = useState('');
  const [ruleCity, setRuleCity] = useState('');
  const [rulePercent, setRulePercent] = useState('');

  useEffect(() => {
    let mounted = true;
    async function loadConfig() {
      setLoading(true);
      setError('');
      try {
        const [supportData, promoData, storesPayload] = await Promise.all([
          settings.getSupportContact(),
          settings.getPromotionsConfig(),
          stores.list({}),
        ]);
        if (!mounted) return;
        const loadedNumbers = Array.isArray(supportData?.whatsapp_numbers) ? supportData.whatsapp_numbers : [];
        setNumbers(loadedNumbers);
        setDefaultNumber(supportData?.default_whatsapp || loadedNumbers[0] || '44999793369');
        setPromoEnabled(Boolean(promoData?.enabled));
        setPromoCode(String(promoData?.code || 'CORREFOOD').toUpperCase());
        setPromoPercent(Number.isFinite(Number(promoData?.discount_percent)) ? Number(promoData.discount_percent) : 0);
        setPromoApplyOnPromotionalProducts(promoData?.apply_on_promotional_products !== false);
        setPromoRules(Array.isArray(promoData?.rules) ? promoData.rules : []);
        setStoresData(Array.isArray(storesPayload) ? storesPayload : []);
      } catch {
        if (mounted) setError('Erro ao carregar configuracoes de suporte');
      } finally {
        if (mounted) setLoading(false);
      }
    }
    loadConfig();
    return () => { mounted = false; };
  }, []);

  const canAddNumber = useMemo(() => digitsOnly(inputNumber).length >= 10, [inputNumber]);
  const cityOptions = useMemo(
    () => [...new Set((storesData || []).map((store) => parseCity(store.address)).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [storesData]
  );

  const handleAddNumber = () => {
    const normalized = digitsOnly(inputNumber);
    if (normalized.length < 10) {
      alert('Digite um numero de WhatsApp valido com DDD.');
      return;
    }
    if (numbers.includes(normalized)) {
      setInputNumber('');
      return;
    }
    const next = [...numbers, normalized];
    setNumbers(next);
    if (!defaultNumber) {
      setDefaultNumber(normalized);
    }
    setInputNumber('');
  };

  const handleRemoveNumber = (phone) => {
    const next = numbers.filter((item) => item !== phone);
    setNumbers(next);
    if (defaultNumber === phone) {
      setDefaultNumber(next[0] || '');
    }
  };

  const handleSave = async () => {
    if (!numbers.length) {
      alert('Adicione pelo menos um numero de suporte.');
      return;
    }
    const defaultPhone = numbers.includes(defaultNumber) ? defaultNumber : numbers[0];
    setSaving(true);
    try {
      await Promise.all([
        settings.updateSupportContact({
          whatsapp_numbers: numbers,
          default_whatsapp: defaultPhone
        }),
        settings.updatePromotionsConfig({
          enabled: Boolean(promoEnabled),
          code: String(promoCode || 'CORREFOOD').trim().toUpperCase(),
          discount_percent: Number.isFinite(Number(promoPercent)) ? Number(promoPercent) : 0,
          apply_on_promotional_products: Boolean(promoApplyOnPromotionalProducts),
          rules: promoRules.map((rule) => ({
            scope_type: String(rule.scope_type || '').toLowerCase(),
            scope_id: String(rule.scope_id || ''),
            enabled: Boolean(rule.enabled),
            code: String(rule.code || promoCode || 'CORREFOOD').toUpperCase(),
            discount_percent: Number.isFinite(Number(rule.discount_percent)) ? Number(rule.discount_percent) : 0,
            apply_on_promotional_products: rule.apply_on_promotional_products !== false,
          })),
        }),
      ]);
      alert('Configuracao de suporte atualizada.');
    } catch (err) {
      alert(err?.response?.data?.detail || 'Nao foi possivel salvar configuracoes.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddPromotionRule = () => {
    const discount = Number(rulePercent);
    if (!Number.isFinite(discount) || discount <= 0 || discount > 100) {
      alert('Informe uma porcentagem valida entre 0 e 100.');
      return;
    }

    const scopeId = ruleMode === 'store' ? ruleStoreId : ruleCity;
    if (!scopeId) {
      alert(ruleMode === 'store' ? 'Selecione a loja para campanha.' : 'Selecione a cidade para campanha.');
      return;
    }

    const nextRule = {
      scope_type: ruleMode,
      scope_id: scopeId,
      enabled: true,
      code: String(promoCode || 'CORREFOOD').trim().toUpperCase() || 'CORREFOOD',
      discount_percent: discount,
      apply_on_promotional_products: Boolean(promoApplyOnPromotionalProducts),
    };

    setPromoRules((prev) => {
      const idx = (prev || []).findIndex(
        (rule) => String(rule.scope_type || '').toLowerCase() === ruleMode
          && normalizeKey(rule.scope_id) === normalizeKey(scopeId)
      );
      if (idx === -1) return [...(prev || []), nextRule];
      const copy = [...prev];
      copy[idx] = nextRule;
      return copy;
    });
    setRulePercent('');
  };

  const handleRemovePromotionRule = (scopeType, scopeId) => {
    setPromoRules((prev) => (prev || []).filter(
      (rule) => !(String(rule.scope_type || '').toLowerCase() === String(scopeType || '').toLowerCase()
        && normalizeKey(rule.scope_id) === normalizeKey(scopeId))
    ));
  };

  const storeById = useMemo(() => {
    const map = new Map();
    (storesData || []).forEach((store) => {
      map.set(String(store.id), store);
    });
    return map;
  }, [storesData]);

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;

  return (
    <div className="admin-section">
      <PageHeader
        title="Configurações"
        subtitle="Parâmetros gerais do sistema"
        actions={(
          <button className="admin-button" onClick={handleSave} disabled={saving}>
            {saving ? 'Salvando...' : 'Salvar'}
          </button>
        )}
      />

      <div className="admin-card">
        <h3>Suporte Correfood (WhatsApp)</h3>
        <p style={{ color: '#B8B8C5', marginBottom: 12 }}>
          O link "Conversar agora com um atendente" da Central de Ajuda usará o número padrão definido abaixo.
        </p>

        <div className="admin-filters">
          <input
            className="admin-input"
            placeholder="Ex: 44 99979-3369"
            value={inputNumber}
            onChange={(e) => setInputNumber(e.target.value)}
          />
          <button className="admin-button secondary" onClick={handleAddNumber} disabled={!canAddNumber}>
            Adicionar número
          </button>
        </div>

        <div style={{ display: 'grid', gap: 10, marginTop: 14 }}>
          {numbers.length === 0 && (
            <p style={{ color: '#B8B8C5' }}>Nenhum número cadastrado.</p>
          )}
          {numbers.map((phone) => (
            <div
              key={phone}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                border: '1px solid var(--admin-border)',
                borderRadius: 10,
                padding: '10px 12px',
                background: 'rgba(255,255,255,0.02)'
              }}
            >
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#FFFFFF' }}>
                <input
                  type="radio"
                  name="default_support"
                  checked={defaultNumber === phone}
                  onChange={() => setDefaultNumber(phone)}
                />
                <span>{formatPhone(phone)}</span>
                {defaultNumber === phone && (
                  <span className="admin-badge success">Padrão</span>
                )}
              </label>

              <button className="admin-button secondary" onClick={() => handleRemoveNumber(phone)}>
                Remover
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="admin-card">
        <h3>Cupons de promocao (Correfood)</h3>
        <p style={{ color: '#B8B8C5', marginBottom: 12 }}>
          Quando ativo, o cupom e aplicado em produtos promocionais e reduz a margem do Correfood nesse pedido.
        </p>

        <div style={{ display: 'grid', gap: 12 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#FFFFFF' }}>
            <input
              type="checkbox"
              checked={promoEnabled}
              onChange={(event) => setPromoEnabled(event.target.checked)}
            />
            <span>Ativar cupom promocional do Correfood</span>
          </label>

          <div className="admin-filters">
            <input
              className="admin-input"
              placeholder="Codigo do cupom (ex: CORREFOOD)"
              value={promoCode}
              onChange={(event) => setPromoCode(event.target.value.toUpperCase())}
            />
            <input
              className="admin-input"
              type="number"
              min="0"
              max="100"
              step="0.1"
              placeholder="Desconto %"
              value={promoPercent}
              onChange={(event) => setPromoPercent(event.target.value)}
            />
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#B8B8C5' }}>
            <input
              type="checkbox"
              checked={promoApplyOnPromotionalProducts}
              onChange={(event) => setPromoApplyOnPromotionalProducts(event.target.checked)}
            />
            <span>Aplicar cupom apenas em produtos marcados como promocionais</span>
          </label>

          <div style={{ borderTop: '1px solid var(--admin-border)', paddingTop: 12 }}>
            <p style={{ color: '#B8B8C5', marginBottom: 8 }}>
              Campanhas por escopo (loja ou cidade)
            </p>
            <div className="admin-filters">
              <select className="admin-select" value={ruleMode} onChange={(event) => setRuleMode(event.target.value)}>
                <option value="store">Por loja</option>
                <option value="city">Por cidade</option>
              </select>

              {ruleMode === 'store' ? (
                <select className="admin-select" value={ruleStoreId} onChange={(event) => setRuleStoreId(event.target.value)}>
                  <option value="">Selecione a loja</option>
                  {(storesData || []).map((store) => (
                    <option key={store.id} value={store.id}>{store.name}</option>
                  ))}
                </select>
              ) : (
                <select className="admin-select" value={ruleCity} onChange={(event) => setRuleCity(event.target.value)}>
                  <option value="">Selecione a cidade</option>
                  {cityOptions.map((city) => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </select>
              )}

              <input
                className="admin-input"
                type="number"
                min="0"
                max="100"
                step="0.1"
                placeholder="Desconto %"
                value={rulePercent}
                onChange={(event) => setRulePercent(event.target.value)}
              />

              <button className="admin-button secondary" type="button" onClick={handleAddPromotionRule}>
                Adicionar campanha
              </button>
            </div>

            <div style={{ display: 'grid', gap: 8, marginTop: 10 }}>
              {!promoRules.length && (
                <span style={{ color: '#B8B8C5' }}>Nenhuma campanha por loja/cidade cadastrada.</span>
              )}
              {promoRules.map((rule) => {
                const isStore = String(rule.scope_type || '').toLowerCase() === 'store';
                const store = isStore ? storeById.get(String(rule.scope_id)) : null;
                const label = isStore ? (store?.name || `Loja ${rule.scope_id}`) : String(rule.scope_id || '');
                return (
                  <div
                    key={`${rule.scope_type}-${rule.scope_id}`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 12,
                      border: '1px solid var(--admin-border)',
                      borderRadius: 10,
                      padding: '8px 12px',
                    }}
                  >
                    <div style={{ display: 'grid', gap: 2 }}>
                      <strong style={{ color: '#fff' }}>{isStore ? 'Loja' : 'Cidade'}: {label}</strong>
                      <span style={{ color: '#B8B8C5', fontSize: 12 }}>
                        {Number(rule.discount_percent || 0).toFixed(2)}% OFF
                      </span>
                    </div>
                    <button
                      className="admin-button secondary"
                      type="button"
                      onClick={() => handleRemovePromotionRule(rule.scope_type, rule.scope_id)}
                    >
                      Remover
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div
        className="admin-card clickable"
        role="button"
        tabIndex={0}
        onClick={() => navigate('/admin/taxas-regras')}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            navigate('/admin/taxas-regras');
          }
        }}
      >
        <h3>Taxas por estabelecimento</h3>
        <p style={{ color: '#B8B8C5', marginBottom: 12 }}>
          Configure percentual por loja e aplique em lote por cidade ou região.
        </p>
        <button className="admin-button secondary" type="button" onClick={(e) => { e.stopPropagation(); navigate('/admin/taxas-regras'); }}>
          Abrir gestão de taxas
        </button>
      </div>
    </div>
  );
}
